import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LINKS, NAV_LINKS } from "@/data/content";

const Nav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 inset-x-0 z-50 border-b border-ocean/10 backdrop-blur-xl bg-[linear-gradient(to_right,#dce8f3_0%,rgba(255,255,255,0.78)_55%)]"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex h-16 md:h-20 items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center self-stretch -ml-6 lg:-ml-10 bg-[#221b88] pl-6 lg:pl-10 pr-16 [mask-image:linear-gradient(to_right,black_55%,transparent_98%)]"
            onClick={() => setOpen(false)}
          >
            <img
              src="/images/tsm-logo.png"
              alt="The Strength Movement, Strength through Science and Psychology"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-ocean ${
                    isActive ? "text-ocean underline decoration-ocean decoration-2 underline-offset-8" : "text-ink/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={LINKS.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-call-button"
              className="hidden xl:inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-ocean"
            >
              Book a Call
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </a>
            <button
              data-testid="nav-mobile-menu-button"
              className="xl:hidden p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden border-t border-ocean/10 bg-gradient-to-b from-[#eaf1f7] to-white backdrop-blur-xl"
            data-testid="nav-mobile-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `font-display text-3xl ${isActive ? "text-ocean italic" : "text-ink"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={LINKS.calendly}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-book-call-button"
                className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white"
              >
                Book a Call
                <ArrowUpRight size={15} strokeWidth={1.5} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
